import { Button } from 'antd';
import { ColumnsType } from 'antd/es/table';
import Table from 'components/Table';
import Text from 'components/Text';
import colorTheme from 'constants/color';
import ticketListActions from 'features/ticketList/services/actions';
import ticketListSelectors from 'features/ticketList/services/selectors';
import { Bill } from 'features/ticketList/services/types/api';
import { ICreditCardTicketItem } from 'features/ticketList/services/types/creditCard';
import moment from 'moment';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import storage from 'utils/sessionStorage';

import ModalConfirm, {
  useModalConfirmRef,
} from 'components/Modal/ModalConfirm';
import ModalDeleteCloseTicket, {
  useModalDeleteCloseTicketRef,
} from 'features/ticketList/components/ModalDeleteClose';
// import AdjustTipButton from './components/Tabs/AdjustTipButton';
// import RefundModalButton from './components/Tabs/RefundModalButton';
import TipColumn from './components/Tabs/TipColumn';
// import VoidModalButton from './components/Tabs/VoidModalButton';
import Box from 'components/Box';

const CreditCardTicketTable = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const modalConfirm = useModalConfirmRef();
  const modalDeleteCloseTicket = useModalDeleteCloseTicketRef();

  const totalElement =
    ticketListSelectors.creditCardTicketsTableData.totalSize();
  const params = ticketListSelectors.creditCardTicketsTableData.params();
  const data = ticketListSelectors.creditCardTicketsTableData.data();
  const loading = ticketListSelectors.creditCardTicketsTableData.loading();

  const columns: ColumnsType<ICreditCardTicketItem> = useMemo(
    () => [
      {
        title: 'Tickets',
        dataIndex: 'ticketNumber',
        width: '4rem',
        align: 'left',
        render(text: number) {
          return (
            <Text
              style={{
                display: 'inline-block',
                padding: '4px 8px',
                background: '#232F3E',
                minWidth: 40,
                gap: 10,
                borderRadius: 4,
                color: '#FFF',
              }}
            >
              {text ? `#${text.toString().padStart(2, '0')}` : '--'}
            </Text>
          );
        },
      },
      {
        title: 'Date & Time',
        dataIndex: 'createdDate',
        align: 'left',
        width: '9rem',
        render(createdDate: string) {
          return (
            <Text
              variant='CONTENT_2'
              color='text_3'
              style={{ whiteSpace: 'nowrap' }}
            >
              {createdDate
                ? moment(createdDate).format('MM-DD-YYYY hh:mmA')
                : '--'}
            </Text>
          );
        },
      },
      {
        title: 'Name',
        dataIndex: 'customerName',
        align: 'left',
        width: '9rem',
        render(customerName: string) {
          return (
            <Text
              variant='CONTENT_2'
              color='text_3'
              style={{ whiteSpace: 'nowrap' }}
            >
              {customerName || 'Walk-In'}
            </Text>
          );
        },
      },
            {
        title: 'Phone',
        dataIndex: 'customerPhone',
        align: 'left',
        width: '8rem',
        render(customerPhone: string) {
          return (
            <Text
              variant='CONTENT_2'
              color='text_3'
              style={{ whiteSpace: 'nowrap' }}
            >
              {customerPhone || '--'}
            </Text>
          );
        },
      },
      {
        title: 'Total',
        dataIndex: 'amount',
        width: '8rem',
        align: 'left',
        render(amount: number) {
          return (
            <Text
              variant='CONTENT_2'
              color='text_3'
              style={{ whiteSpace: 'nowrap' }}
            >
              {formatCurrency(amount)}
            </Text>
          );
        },
      },
      {
        title: 'Tips',
        dataIndex: 'tip',
        width: '12rem',
        align: 'left',
        render(tip: any, record: ICreditCardTicketItem) {
          return !['REFUND', 'VOID'].includes(record.status) ? (
            <TipColumn
              record={{ billId: record.billId, tip: record.tip } as Bill}
            />
          ) : (
            <Text
              variant='CONTENT_2'
              color='text_3'
              style={{ whiteSpace: 'nowrap' }}
            >
              {formatCurrency(tip)}
            </Text>
          );
        },
      },
      {
        title: 'Fees',
        dataIndex: 'feeCredit',
        width: '8rem',
        align: 'left' as any,
        render(feeCredit: number) {
          return (
            <Text variant='CONTENT_2' color='error_4'>
              {formatCurrency(feeCredit) || '--'}
            </Text>
          );
        },
      },
      {
        title: 'Last 4 No.',
        dataIndex: 'last4',
        width: '8rem',
        align: 'left',
        render(last4: string) {
          return (
            <Text
              variant='CONTENT_2'
              color='text_3'
              style={{ whiteSpace: 'nowrap' }}
            >
              {last4 ?? '---'}
            </Text>
          );
        },
      },
      {
        title: 'Batch#',
        dataIndex: 'batchNum',
        width: '8rem',
        align: 'left',
        render(batchNum: string) {
          return (
            <Text
              variant='CONTENT_2'
              color='text_3'
              style={{ whiteSpace: 'nowrap' }}
            >
              {batchNum ?? '---'}
            </Text>
          );
        },
      },
      {
        title: 'Trans#',
        dataIndex: 'transactionNumber',
        width: '8rem',
        align: 'left',
        render(transactionNumber: string) {
          return (
            <Text
              variant='CONTENT_2'
              color='text_3'
              style={{ whiteSpace: 'nowrap' }}
            >
              {transactionNumber ? `#${transactionNumber}` : '---'}
            </Text>
          );
        },
      },
      {
        title: 'Card Type',
        dataIndex: 'cardType',
        width: '8rem',
        align: 'left',
        render(cardType: string) {
          return (
            <Text
              variant='CONTENT_2'
              color='text_3'
              style={{ whiteSpace: 'nowrap' }}
            >
              {cardType ?? '---'}
            </Text>
          );
        },
      },
      {
        title: 'Entry Type',
        dataIndex: 'entryType',
        width: '8rem',
        align: 'left',
        render(entryType: string) {
          return (
            <Text
              variant='CONTENT_2'
              color='text_3'
              style={{ whiteSpace: 'nowrap' }}
            >
              {entryType ?? '---'}
            </Text>
          );
        },
      },
      {
        title: 'Auth Code',
        dataIndex: 'authCode',
        width: '8rem',
        align: 'left',
        render(authCode: string) {
          return (
            <Text
              variant='CONTENT_2'
              color='text_3'
              style={{ whiteSpace: 'nowrap' }}
            >
              {authCode ?? '---'}
            </Text>
          );
        },
      },
      {
        title: 'Actions',
        width: 150,
        dataIndex: '',
        render(_: any, record: ICreditCardTicketItem) {
          return (
            <ActionBoxStyled>
              {/* <AdjustTipButton v2 record={{ billId: record.billId } as Bill} />
              {record.status == 'CLOSED' && (
                <>
                  {record.refund && (
                    <RefundModalButton
                      v2
                      billId={record.billId}
                      maxAmount={record.amount}
                    />
                  )}
                  {record.void && (
                    <VoidModalButton
                      v2
                      isCreditCard
                      billDetail={
                        {
                          billId: record.billId,
                          billStatus: record.status,
                        } as Bill
                      }
                    />
                  )}
                </>
              )} */}
              <Box display='flex' alignItems='center'>
                <ActionButton
                  color={colorTheme.fill_1}
                  bgColor={colorTheme.info_3}
                  onClick={() =>
                    navigate(
                      `/store/${storage.shop_id.get()}/tickets/${record.billId}`
                    )
                  }
                >
                  DETAILS
                </ActionButton>
              </Box>
            </ActionBoxStyled>
          );
        },
      },
    ],
    [navigate]
  );

  const handleChangePage = (page: number, size?: number) => {
    dispatch(
      ticketListActions.setCreditCardParams({
        ...params,
        page: page,
        size,
      })
    );
  };

  const rowClassName = (record: ICreditCardTicketItem) => {
    if (record.status === 'CLOSED') {
      return 'CLOSED CREDIT_PAGE';
    }
    if (record.status === 'REFUND') {
      return ' REFUND CREDIT_PAGE';
    }
    if (record.status === 'VOID') {
      return ' VOID CREDIT_PAGE';
    }
    return 'CLOSED CREDIT_PAGE';
  };

  return (
    <CreditCardTicketTableStyled>
      <Table
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey={'billId'}
        rowClassName={rowClassName}
        pagination={{
          total: totalElement ?? 0,
          pageSize: params.size,
          current: params.page,
          showSizeChanger: false,
        }}
        handleChangePage={handleChangePage}
        showSizeChanger
      />
      <ModalConfirm ref={modalConfirm} />
      <ModalDeleteCloseTicket v2 ref={modalDeleteCloseTicket} />
    </CreditCardTicketTableStyled>
  );
};

export default CreditCardTicketTable;

const CreditCardTicketTableStyled = styled.div`
  .ant-table-tbody .ant-table-row {
    td {
      border-bottom: 1px solid #c9cdd4;
      border-right: 1px solid white;
    }
    &:hover td {
      background: #c2d6da94 !important;
    }
  }
  .ant-table-tbody .ant-table-row.VOID {
    background-color: #f7fffd;
    td {
      border-bottom: 1px solid #346751 !important;
    }
    &:hover td {
      background-color: #9fd9cbdb !important;
    }
  }

  .ant-table-tbody .ant-table-row.REFUND {
    background-color: #fffaf8;
    td {
      border-bottom: 1px solid #c84b31 !important;
    }
    &:hover td {
      background-color: #ffc0a9e3 !important;
    }
  }
  .ant-table-cell {
    padding: 0.5rem !important;
  }

  th.ant-table-cell {
    border-right: 1px solid #fff;
    text-align: center !important;
    padding: 14px 0.5rem !important;

    &:nth-last-child(1) {
      border-right: none;
    }
  }

  .ant-table-wrapper .ant-pagination {
    position: sticky;
    bottom: 0;
    border: 0;

    .ant-pagination-options {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);

      .ant-select.ant-pagination-options-size-changer {
        height: 40px;

        .ant-select-selector {
          padding: 0 20px;
        }
      }

      .ant-select {
        .ant-select-arrow {
          color: #1d2129;
          font-size: 18px;
          transform: scaleX(1.1) scaleY(0.9);
        }

        .ant-select-selector {
          background: #ffffff;
          border: 1px solid #86909c;

          .ant-select-selection-item-content,
          .ant-select-selection-placeholder,
          .ant-select-selection-search-input,
          .ant-select-selection-item {
            color: #1d2129;
            font-family: Poppins;
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
          }
        }
      }
    }
  }

  .ant-table-wrapper .ant-table .ant-table-content {
    max-height: unset;
    overflow: unset;

    .ant-table-thead th.ant-table-cell,
    .ant-table-thead td.ant-table-cell {
      height: 48px;
      padding: 0px 8px;
      gap: 10px;
      align-self: stretch;
      border-bottom: 1px solid #ccd4dc !important;
      background: #6fabb6 !important;
      color: #fff !important;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      text-transform: capitalize;
      text-align: left !important;
      position: sticky;
      top: 0;

      &::before {
        width: 1.5px;
        height: 100%;
      }
    }

    .ant-table-tbody td.ant-table-cell {
      .BODY_1,
      .CONTENT_2 {
        color: #1d2129;
        font-family: Poppins;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
      }
    }
  }
`;

const ActionButton = styled(Button).withConfig({
  shouldForwardProp: (prop) => !['bgColor'].includes(prop),
})<{ bgColor?: string; color?: string }>`
  display: flex;
  width: fit-content;
  height: 38px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  flex: 1;
  text-align: center;
  font-size: 14px;
  font-weight: 600;

  background: #6fabb6 !important;
  color: #fff !important;
  border-color: #6fabb6 !important;
  box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.4),
    0px 4px 8px -3px rgba(45, 35, 66, 0.3), 0px -4px 0px 0px #144b55 inset !important;

  .CONTENT_2,
  span {
    color: #fff !important;
    text-align: center;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
  }

  ${(props) =>
    props.bgColor === colorTheme.error_2 &&
    `
    background: #FF8890 !important;
    box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.40), 0px 4px 8px -3px rgba(45, 35, 66, 0.30), 0px -4px 0px 0px #E7646D inset !important;
  `}

  ${(props) =>
    props.bgColor === colorTheme.fill_2 &&
    `
    background: #E5E6EB !important;
    box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.40), 0px 4px 8px -3px rgba(45, 35, 66, 0.30), 0px -3px 0px 0px #828295 inset !important;
    border: none;
    
    .CONTENT_2, span {
      color: #1D2129 !important;
    }
  `}
  
  ${(props) =>
    props.bgColor === colorTheme.info_3 &&
    `
    background: #FFF !important;
    border: 1.5px solid #2D6772 !important;
    box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.40), 0px 4px 8px -3px rgba(45, 35, 66, 0.30), 0px -3px 0px 0px #144B55 inset !important;
    
    .CONTENT_2, span {
      color: #2D6772 !important;
    }
  `}
`;

const ActionBoxStyled = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: nowrap;
`;
